import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingCart } from "lucide-react";
import { formatPrice, getProductImage, PRODUCT_PLACEHOLDER } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function ProductCard({ product, testIdPrefix = "product" }) {
  const { addItem } = useCart();
  const img = getProductImage(product);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, 1);
    toast.success("Added to cart", { description: product.name });
  };

  return (
    <article
      className="group card-elevated flex flex-col overflow-hidden rounded-2xl"
      data-testid={`${testIdPrefix}-card-${product.id}`}
    >
      <Link
        to={`/product/${product.id}`}
        className="relative block aspect-[4/3] overflow-hidden bg-slate-100"
        data-testid={`${testIdPrefix}-link-${product.id}`}
      >
        <img
          src={img}
          alt={product.name}
          loading="lazy"
          onError={(e) => {
            if (!e.currentTarget.src.endsWith(PRODUCT_PLACEHOLDER)) e.currentTarget.src = PRODUCT_PLACEHOLDER;
          }}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
        />
        <div className="absolute left-3 top-3 flex gap-1.5">
          <span className="chip bg-white/95 text-brand-navy shadow-sm">
            {product.category}
          </span>
        </div>
        {!product.in_stock && (
          <div className="absolute inset-0 flex items-center justify-center bg-brand-navy-dark/60 text-white">
            <span className="chip bg-white text-brand-navy">Out of Stock</span>
          </div>
        )}
        {product.in_stock && product.stock <= 5 && (
          <span className="absolute right-3 top-3 chip bg-brand-teal text-white">
            Only {product.stock} left
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <Link
          to={`/product/${product.id}`}
          className="line-clamp-2 text-sm font-semibold text-brand-navy transition hover:text-brand-teal"
        >
          {product.name}
        </Link>
        <div className="mt-auto flex items-end justify-between pt-3">
          <span className="font-display text-xl font-extrabold text-brand-navy">
            {formatPrice(product.price)}
          </span>
          <button
            type="button"
            onClick={handleAdd}
            disabled={!product.in_stock}
            className="inline-flex items-center gap-1.5 rounded-full btn-accent px-3 py-2 text-xs font-bold uppercase tracking-wide disabled:cursor-not-allowed disabled:bg-slate-300"
            data-testid={`add-to-cart-${product.id}`}
          >
            <ShoppingCart size={14} />
            Add
          </button>
        </div>
      </div>
    </article>
  );
}
